// Configuración de entorno para la aplicación
const resolvedApiBaseUrl =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://medicare-api.dswsolutions.io/api'
    : 'http://localhost:5000/api');

export const config = {
  // Configuración de la aplicación
  app: {
    name: process.env.REACT_APP_NAME || 'MediCare',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    description:
      process.env.REACT_APP_DESCRIPTION || 'Sistema de gestión médica integral',
  },

  // Configuración de la API
  api: {
    baseURL: resolvedApiBaseUrl,
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000'),
    retryAttempts: parseInt(process.env.REACT_APP_API_RETRY_ATTEMPTS || '3'),
  },

  // Configuración de autenticación
  auth: {
    sessionDurationMinutes: parseInt(
      process.env.REACT_APP_SESSION_DURATION_MINUTES || '10'
    ),
    autoLogoutEnabled: process.env.REACT_APP_AUTO_LOGOUT_ENABLED === 'true',
  },

  // Configuración de notificaciones
  notifications: {
    duration: parseInt(process.env.REACT_APP_NOTIFICATION_DURATION || '2000'),
    position: process.env.REACT_APP_NOTIFICATION_POSITION || 'top-right',
    maxVisible: parseInt(process.env.REACT_APP_NOTIFICATION_MAX_VISIBLE || '5'),
  },

  // Configuración de paginación
  pagination: {
    defaultPageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE || '10'),
    maxPageSize: parseInt(process.env.REACT_APP_MAX_PAGE_SIZE || '100'),
  },

  // Configuración de búsqueda
  search: {
    debounceMs: parseInt(process.env.REACT_APP_SEARCH_DEBOUNCE_MS || '300'),
    maxResults: parseInt(process.env.REACT_APP_MAX_SEARCH_RESULTS || '5'),
  },

  // Configuración de turnos
  appointments: {
    year: parseInt(process.env.REACT_APP_APPOINTMENT_YEAR || '2025'),
    perDay: {
      min: parseInt(process.env.REACT_APP_APPOINTMENTS_PER_DAY_MIN || '0'),
      max: parseInt(process.env.REACT_APP_APPOINTMENTS_PER_DAY_MAX || '15'),
    },
    workingHours: {
      start: parseInt(process.env.REACT_APP_WORKING_HOURS_START || '8'),
      end: parseInt(process.env.REACT_APP_WORKING_HOURS_END || '19'),
    },
    workingDaysPerYear: parseInt(
      process.env.REACT_APP_WORKING_DAYS_PER_YEAR || '260'
    ),
  },

  // Configuración de historias clínicas
  medicalRecords: {
    perSpecialty: parseInt(
      process.env.REACT_APP_MEDICAL_RECORDS_PER_SPECIALTY || '25'
    ),
  },

  // Configuración de pacientes
  patients: {
    perSpecialty: parseInt(
      process.env.REACT_APP_PATIENTS_PER_SPECIALTY || '25'
    ),
  },

  // Configuración de actualizaciones
  updates: {
    remainingAppointmentsInterval: parseInt(
      process.env.REACT_APP_REMAINING_APPOINTMENTS_UPDATE_INTERVAL || '60000'
    ),
    notificationsInterval: parseInt(
      process.env.REACT_APP_NOTIFICATIONS_UPDATE_INTERVAL || '300000'
    ), // Cambiado de 30 segundos a 5 minutos
  },

  // Configuración de desarrollo
  development: {
    debugMode: process.env.REACT_APP_DEBUG_MODE === 'true',
    logLevel: process.env.REACT_APP_LOG_LEVEL || 'error',
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
  },

  // Configuración de temas
  theme: {
    default: process.env.REACT_APP_DEFAULT_THEME || 'system',
    enableAnimations: process.env.REACT_APP_ENABLE_ANIMATIONS !== 'false',
  },

  // Configuración de archivos
  files: {
    maxSizeMB: parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB || '5'),
    allowedTypes:
      process.env.REACT_APP_ALLOWED_FILE_TYPES ||
      'image/jpeg,image/png,image/gif,application/pdf',
  },

  // Configuración de seguridad
  security: {
    enableHttpsRedirect: process.env.REACT_APP_ENABLE_HTTPS_REDIRECT === 'true',
    enableContentSecurityPolicy:
      process.env.REACT_APP_ENABLE_CONTENT_SECURITY_POLICY === 'true',
  },

  // Configuración de deployment
  deployment: {
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    buildNumber: process.env.REACT_APP_BUILD_NUMBER || '1',
    buildDate: process.env.REACT_APP_BUILD_DATE || new Date().toISOString(),
    version: process.env.REACT_APP_DEPLOYMENT_VERSION || '1.0.0',
  },

  // Configuración de autenticación con Google (API separada)
  googleAuth: {
    baseURL: process.env.REACT_APP_GOOGLE_AUTH_API_URL || '',
  },

  // Configuración de hosting (Somee)
  hosting: {
    provider: process.env.REACT_APP_HOSTING_PROVIDER || 'somee',
    domain: process.env.REACT_APP_HOSTING_DOMAIN || '',
    ftp: {
      host: process.env.REACT_APP_FTP_HOST || '',
      username: process.env.REACT_APP_FTP_USERNAME || '',
      password: process.env.REACT_APP_FTP_PASSWORD || '',
      port: parseInt(process.env.REACT_APP_FTP_PORT || '21'),
      remoteDir: process.env.REACT_APP_FTP_REMOTE_DIR || '/public_html/',
    },
  },
};

// Función para obtener configuración específica
export const getConfig = <T>(path: string): T => {
  const keys = path.split('.');
  let value: any = config;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      throw new Error(`Config path "${path}" not found`);
    }
  }

  return value as T;
};

// Función para validar configuración
export const validateConfig = (): void => {
  const requiredConfigs = [
    'api.baseURL',
    'auth.sessionDurationMinutes',
    'appointments.year',
    'medicalRecords.perSpecialty',
  ];

  for (const configPath of requiredConfigs) {
    try {
      getConfig(configPath);
    } catch (error) {
      // Configuración no encontrada, usar valores por defecto
    }
  }
};

// Exportar configuración por defecto
export default config;
